import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { debounceTime, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as fromActions from './actions';
import { OrganizationService, UserManagementService } from '@services/api/api.service';
import { UserRole } from '@models/user';
import { UserPreferences } from '@shared/_types/UserPreferences';
import { selectUser, selectUserPreferences } from './index';
import { promiseWrapper } from '@shared/_funtions/promiseWarapper.function';

@Injectable()
export class CommonEffects {

  syncUserPreferences$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setUserPreferences),
    filter(action => !action.skipSync),
    debounceTime(1000),
    switchMap(action =>
      this.userManagementService.patchUiPreferences(action.value),
    ),
  ), { dispatch: false });

  // Fetch preferences for all users; fetch organizations only for customer users.
  public getOrganizationsAndPreferences$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setUser),
    switchMap(({ value: user }) => {
      if (!this.isCustomerUser(user)) {
        return forkJoin({
          userPreferences: this.userManagementService.getUiPreferences(),
          userVehicleFavorites: this.userManagementService.getUserVehicleFavorites(),
        }).pipe(
          switchMap(({ userPreferences, userVehicleFavorites }) => [
            fromActions.setUserPreferences({ value: userPreferences as UserPreferences }),
            fromActions.setUserVehicleFavorites({ value: userVehicleFavorites || [] }),
            fromActions.setSelectedOrganizationId({ value: null }),
            fromActions.setOrganizations({ value: [] }),
            fromActions.setOrganizationsLoaded({ value: true }),
          ]),
        );
      }

      return forkJoin({
        organizations: this.organizationService.getOrganizations(),
        userPreferences: this.userManagementService.getUiPreferences(),
        userVehicleFavorites: this.userManagementService.getUserVehicleFavorites(),
      }).pipe(
        switchMap(({ organizations, userPreferences, userVehicleFavorites }) => {
          const orgs = organizations || [];
          const savedOrgId = userPreferences?.selectedOrganizationId;
          const savedOrgExists = savedOrgId && orgs.some(org => org.id === savedOrgId);
          const selectedOrganizationId = savedOrgExists
            ? savedOrgId
            : (orgs.length > 0 ? orgs[0].id : null);

          return [
            fromActions.setUserPreferences({ value: userPreferences as UserPreferences }),
            fromActions.setUserVehicleFavorites({ value: userVehicleFavorites || [] }),
            fromActions.setSelectedOrganizationId({ value: selectedOrganizationId }),
            fromActions.setOrganizations({ value: orgs }),
            fromActions.setOrganizationsLoaded({ value: true }),
          ];
        }),
      );
    }),
  ));

  getUserVehicleFavorites$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getUserVehicleFavorites),
    switchMap(() => this.userManagementService.getUserVehicleFavorites().pipe(
      switchMap((userVehicleFavorites) => [
        fromActions.setUserVehicleFavorites({ value: userVehicleFavorites || [] }),
      ]),
    )),
  ));

  getOrganizations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getOrganizations),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(async ([_, user]) => {
        if (!this.isCustomerUser(user)) {
          return [
            fromActions.setSelectedOrganizationId({ value: null }),
            fromActions.setOrganizations({ value: [] }),
          ];
        }

        const organizations = await promiseWrapper(this.organizationService.getOrganizations());
        return [fromActions.setOrganizations({ value: organizations })];
      }),
      switchMap((actions) => [...actions]),
    );
  });

  // Save selected organization to user preferences when it changes
  saveSelectedOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setSelectedOrganizationId),
    withLatestFrom(this.store.select(selectUserPreferences)),
    map(([action, userPreferences]) => {
      // Only update if the organization ID actually changed
      if (userPreferences?.selectedOrganizationId !== action.value) {
        return fromActions.setUserPreferences({
          value: { ...userPreferences, selectedOrganizationId: action.value }
        });
      }
      return null;
    }),
    // Filter out null actions
    switchMap(action => action ? [action] : []),
  ));

  private isCustomerUser(user: any): boolean {
    const roles = user?.roles as string[] | undefined;
    return Array.isArray(roles) && roles.includes(UserRole.Customer);
  }

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    public userManagementService: UserManagementService,
    public organizationService: OrganizationService,
  ) {
  }
}
